import { isNonEmptyString } from "@clipboard-health/util-ts";

const MAX_ERROR_LINES = 3;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function unescapeJsonPointerSegment(segment: string): string {
  // RFC 6901: `~1` decodes to `/`, `~0` decodes to `~`. Order matters: decode
  // `~1` first so a literal `~01` round-trips correctly.
  return segment.replaceAll("~1", "/").replaceAll("~0", "~");
}

function propertyKeyFromPointer(pointer: unknown): string | undefined {
  if (!isNonEmptyString(pointer) || pointer === "/") {
    return undefined;
  }
  const lastSlash = pointer.lastIndexOf("/");
  const lastSegment = lastSlash === -1 ? pointer : pointer.slice(lastSlash + 1);
  if (lastSegment === "") {
    return undefined;
  }
  return unescapeJsonPointerSegment(lastSegment);
}

function detailFromJsonApiEntry(entry: unknown): string | undefined {
  if (!isRecord(entry)) {
    return undefined;
  }
  if (isNonEmptyString(entry.detail)) {
    return entry.detail;
  }
  if (isNonEmptyString(entry.title)) {
    return entry.title;
  }
  return undefined;
}

function pointerFromJsonApiEntry(entry: unknown): string | undefined {
  if (!isRecord(entry) || !isRecord(entry.source)) {
    return undefined;
  }
  return propertyKeyFromPointer(entry.source.pointer);
}

function formatJsonApiErrors(errors: readonly unknown[]): string | undefined {
  const formatted: string[] = [];
  for (const entry of errors) {
    const detail = detailFromJsonApiEntry(entry);
    if (detail === undefined) {
      continue;
    }
    const key = pointerFromJsonApiEntry(entry);
    formatted.push(key === undefined ? detail : `${key}: ${detail}`);
  }

  if (formatted.length === 0) {
    return undefined;
  }

  if (formatted.length <= MAX_ERROR_LINES) {
    return formatted.join("\n");
  }

  const overflow = formatted.length - MAX_ERROR_LINES;
  return [...formatted.slice(0, MAX_ERROR_LINES), `…and ${overflow} more`].join("\n");
}

export function extractErrorDetail(error: unknown): string | undefined {
  if (!isRecord(error)) {
    return undefined;
  }

  let body: unknown;
  if ("response" in error && isRecord(error.response)) {
    body = "data" in error.response ? error.response.data : error.response.body;
  }

  if (isRecord(body)) {
    if ("displayableMessage" in body && isNonEmptyString(body.displayableMessage)) {
      return body.displayableMessage;
    }
    if ("errors" in body && Array.isArray(body.errors)) {
      const formatted = formatJsonApiErrors(body.errors);
      if (formatted !== undefined) {
        return formatted;
      }
    }
    if ("message" in body && isNonEmptyString(body.message)) {
      return body.message;
    }
    if ("error" in body && isNonEmptyString(body.error)) {
      return body.error;
    }
  } else if (isNonEmptyString(body) && !body.trimStart().startsWith("<")) {
    // Axios can deliver plain-string error bodies in `response.data` when the
    // backend returns a non-JSON payload. Skip HTML to avoid rendering raw
    // markup in a toast.
    return body;
  }

  // Fall back to superagent's plain-text error body when the structured body
  // is absent or doesn't contain a recognized message field. Skip empty
  // strings and HTML payloads (e.g. 502 gateway pages).
  if (
    "response" in error &&
    isRecord(error.response) &&
    "text" in error.response &&
    isNonEmptyString(error.response.text) &&
    !error.response.text.trimStart().startsWith("<")
  ) {
    return error.response.text;
  }

  if ("message" in error && isNonEmptyString(error.message)) {
    return error.message;
  }

  return undefined;
}
